import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, FileText, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const stats = [
    { number: '25+', label: 'Années d\'expérience' },
    { number: '100%', label: 'Satisfaction client' },
    { number: '48h', label: 'Délai de réponse' }
  ];

  const features = [
    {
      icon: Building2,
      title: 'Expertise complète',
      description: 'Accompagnement sur tous types d\'autorisations d\'urbanisme'
    },
    {
      icon: FileText,
      title: 'Dossiers complets',
      description: 'Plans 2D/3D, documents administratifs et techniques'
    },
    {
      icon: Shield,
      title: 'Garantie qualité',
      description: 'Respect des règles d\'urbanisme et validation assurée'
    },
    {
      icon: Clock,
      title: 'Rapidité d\'exécution',
      description: 'Traitement rapide sans perte de temps'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-gray-50 -z-10" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-pink-100 rounded-full blur-3xl opacity-30 -z-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-100 rounded-full blur-3xl opacity-30 -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center">
              <div className="flex items-center justify-center gap-4">
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  L'autorisation d'urbanisme, <span className="text-pink-600">la clé de votre projet</span>
                </h1>
                <video src="https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/nk3b9zei_logo-U2C.mp4" autoPlay loop muted playsInline className="h-24 w-auto flex-shrink-0 hidden sm:block rounded-lg" />
              </div>
              
              <h2 className="text-2xl font-semibold text-gray-900">
                Accompagnement sur toutes démarches administratives
              </h2>
              
              <div className="flex flex-wrap justify-center gap-3">
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Permis de construire</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Déclaration préalable</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Certificat d'urbanisme</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Permis d'aménager</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Permis de démolir</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Assainissement</span>
                <span className="px-4 py-2 bg-white rounded-full shadow-md text-sm font-bold text-gray-800">Régularisation de travaux</span>
              </div>
              
              <p className="text-center text-xl font-bold text-pink-600 pt-4 animate-pulse" style={{animation: 'pulseScale 2s ease-in-out infinite'}}>
                <Link to="/contact" className="inline-block px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                  Etude de faisabilité offerte pour votre projet !!
                </Link>
              </p>
              <style>{`@keyframes pulseScale { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>

              <div className="flex flex-wrap justify-center gap-4 pt-4">
                <Button asChild size="lg" className="bg-pink-600 hover:bg-pink-700 text-white shadow-lg transition-all duration-300 hover:scale-105">
                  <Link to="/contact" className="flex items-center">
                    DEMANDE DE DEVIS
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-2 border-pink-600 text-pink-600 hover:bg-pink-50 transition-all duration-300">
                  <Link to="/services">MES SERVICES</Link>
                </Button>
              </div>
              
              <div className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto space-y-3">
                <p>
                  Vous avez l'intention de réaliser des travaux dans un avenir proche : construction d'une maison, d'une annexe (garage, carport, piscine, véranda...), extension, modification de façades...
                </p>
                <p>
                  <span className="font-bold">Expert en autorisations d'urbanisme depuis plus de 25 ans en cabinets d'architecte</span>, je suis spécialisé dans l'accompagnement des particuliers et des professionnels. Pour garantir la validation de votre projet, je vous suggère la solution adaptée en vous offrant des conseils et des conceptions personnalisés, en prenant en compte l'analyse du PLU, pour garantir la validation de votre projet.
                </p>
              </div>

              <div className="flex items-center justify-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-600">Disponible</span>
                </div>
                <div className="h-8 w-px bg-gray-300" />
                <a href="tel:0761926962" className="text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                  07.61.92.69.62
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_d46b51c3-4a6b-4717-9ad0-d85d730ee000/artifacts/txj058r7_iamage%20-23.01.26.jpg"
                alt="Projet d'urbanisme"
                className="w-full h-auto rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent rounded-2xl" />
              
              {/* Panneau de chantier offert */}
              <div className="absolute top-4 left-4 transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_d46b51c3-4a6b-4717-9ad0-d85d730ee000/artifacts/1hr225qx_panneau%20chantier%20avec%20logo.jpg"
                    alt="Panneau de chantier"
                    className="w-48 h-auto rounded-lg shadow-2xl"
                  />
                  <div className="absolute -top-3 -right-3 bg-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                    Panneau de chantier offert !!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-pink-400 mb-2">{stat.number}</p>
                <p className="text-gray-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Pourquoi choisir URBA-2C ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une expertise reconnue au service de vos projets d'urbanisme
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-pink-200 hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-pink-600 transition-colors duration-300">
                      <Icon className="w-7 h-7 text-pink-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-600 to-pink-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Vous avez un projet ? Parlons-en !
          </h2>
          <p className="text-xl mb-8 text-pink-100">
            N'hésitez pas à me contacter pour discuter de votre projet. 
            Je suis là pour vous conseiller et vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-pink-600 hover:bg-gray-100 shadow-xl transition-all duration-300 hover:scale-105">
              <a href="tel:0761926962" className="flex items-center">
                Appelez-moi
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-pink-600 transition-all duration-300">
              <Link to="/contact">Envoyer un message</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;